/* ==========================================================================
   Silver Play — theme.js
   Vanilla replacements for the Next.js source's React/Framer Motion/GSAP/
   Lenis behaviour. No dependencies, no build step.
   ========================================================================== */
(function () {
  "use strict";

  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var $ = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };
  var clamp = function (v, a, b) { return Math.min(Math.max(v, a), b); };

  /* ---------------------------------------------------------------- money */
  var money = function (cents) {
    var f = ((window.SilverPlay && window.SilverPlay.moneyFormat) || "₹{{amount_no_decimals}}").replace(/<[^>]*>/g, "");
    var amount = cents / 100;
    var noDec = Math.round(amount).toLocaleString("en-IN");
    var withDec = amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return f.replace(/\{\{\s*amount_no_decimals[^}]*\}\}/g, noDec)
            .replace(/\{\{\s*amount[^}]*\}\}/g, withDec);
  };

  /* ------------------------------------------------- scroll reveal engine */
  function initReveal(root) {
    window.__spRevealReady = true;
    var targets = $$("[data-reveal],[data-stagger],[data-split]", root || document);
    if (!targets.length) return;
    if (reduced || !("IntersectionObserver" in window)) {
      targets.forEach(function (el) { el.classList.add("is-in"); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        // intersectionRatio is visible area / the ELEMENT's area, so anything
        // taller than the viewport can never reach 0.15. Reveal those as soon
        // as they enter; keep the 15% trigger for normal-sized elements.
        var tall = e.boundingClientRect.height > window.innerHeight * 0.9;
        if (!tall && e.intersectionRatio < 0.15) return;
        var el = e.target;
        if (el.hasAttribute("data-stagger")) {
          var step = parseFloat(el.getAttribute("data-stagger")) || 0.09;
          Array.prototype.forEach.call(el.children, function (child, i) {
            child.style.transitionDelay = (i * step) + "s";
          });
        }
        el.classList.add("is-in");
        io.unobserve(el);
      });
    }, { threshold: [0, 0.15], rootMargin: "0px 0px -8% 0px" });
    targets.forEach(function (el) {
      var d = el.getAttribute("data-delay");
      if (d) el.style.setProperty("--reveal-delay", d + "s");
      io.observe(el);
    });
  }

  /* --------------------------------------------- split text (word masks) */
  function initSplit(root) {
    $$("[data-split]", root || document).forEach(function (el) {
      if (el.dataset.splitDone) return;
      el.dataset.splitDone = "1";
      var text = el.textContent.trim();
      var by = el.getAttribute("data-split") === "char" ? "char" : "word";
      var units = by === "word" ? text.split(/\s+/) : Array.from(text);
      var step = by === "word" ? 0.075 : 0.028;
      var base = parseFloat(el.getAttribute("data-delay")) || 0;
      el.setAttribute("aria-label", text);
      var span = document.createElement("span");
      span.className = "split";
      span.setAttribute("aria-hidden", "true");
      units.forEach(function (u, i) {
        var mask = document.createElement("span");
        mask.className = "split__mask";
        var inner = document.createElement("span");
        inner.className = "split__unit";
        inner.textContent = by === "word" && i < units.length - 1 ? u + " " : u;
        inner.style.transitionDelay = (base + i * step) + "s";
        mask.appendChild(inner);
        span.appendChild(mask);
      });
      el.textContent = "";
      el.appendChild(span);
    });
  }

  /* ---------------------------------------------------- cinehero slider */
  function initCineheroSlider() {
    $$("[data-cinehero-slider]").forEach(function (root) {
      var slides = $$("[data-cinehero-slide]", root);
      if (slides.length < 2) return;
      var dotsWrap = $("[data-cinehero-dots]", root);
      var active = 0, timer;

      function setActive(i) {
        active = (i + slides.length) % slides.length;
        slides.forEach(function (s, j) { s.classList.toggle("is-active", j === active); });
        if (dots.length) dots.forEach(function (d, j) { d.classList.toggle("is-active", j === active); });
      }
      function next() { setActive(active + 1); }
      function restart() {
        clearInterval(timer);
        if (!reduced) timer = setInterval(next, 3000);
      }

      var dots = [];
      if (dotsWrap) {
        slides.forEach(function (_, i) {
          var dot = document.createElement("button");
          dot.type = "button";
          dot.className = "cinehero__dot" + (i === 0 ? " is-active" : "");
          dot.setAttribute("aria-label", "Show slide " + (i + 1));
          dot.addEventListener("click", function () { setActive(i); restart(); });
          dotsWrap.appendChild(dot);
          dots.push(dot);
        });
      }
      restart();

      /* Touch swipe (mobile) — left swipe advances, right swipe goes back.
         Only reacts to a mostly-horizontal drag past a small threshold, so
         it doesn't hijack a vertical page scroll. */
      var touchX = 0, touchY = 0, dragging = false;
      var stage = root.querySelector(".cinehero__stage") || root;
      stage.addEventListener("touchstart", function (e) {
        var t = e.changedTouches[0];
        touchX = t.clientX; touchY = t.clientY; dragging = true;
      }, { passive: true });
      stage.addEventListener("touchend", function (e) {
        if (!dragging) return;
        dragging = false;
        var t = e.changedTouches[0];
        var dx = t.clientX - touchX, dy = t.clientY - touchY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          setActive(active + (dx < 0 ? 1 : -1));
          restart();
        }
      }, { passive: true });
    });
  }

  /* ------------------------------------- offer band reel/card heights */
  function initOfferReelHeight() {
    $$(".offer-split__media").forEach(function (media) {
      var card = media.querySelector(".offer-split__image--card");
      var reel = media.querySelector(".offer-split__image--reel");
      if (!card || !reel) return;
      /* Measure .card__frame (the actual visible card — image + title +
         price + button), not the outer .offer-split__image--card wrapper.
         The wrapper can end up shorter than its own content (e.g. a CSS
         rule elsewhere clipping it while overflow:visible lets the real
         content spill out past that box) — matching against the wrapper
         then silently matches the wrong, too-small number. The inner
         frame's own rendered size is always the true visible height. */
      var cardInner = card.querySelector(".card__frame") || card;
      function match() {
        /* Below 900px the card is hidden entirely (CSS) and the reel
           takes the full row width on its own — nothing to match against,
           and matching anyway would zero the reel out (a hidden element's
           bounding rect is 0x0). */
        if (card.offsetParent === null) { reel.style.width = ""; reel.style.height = ""; return; }
        var rect = cardInner.getBoundingClientRect();
        reel.style.width = rect.width + "px";
        reel.style.height = rect.height + "px";
      }
      /* Fonts/images finishing load after the first paint can shift the
         card's height slightly after match() already ran once — re-measure
         a few times over the following couple seconds as a safety net,
         on top of the ResizeObserver (which itself won't fire for every
         cause of a height change, e.g. a web font swap reflowing text
         without the browser treating it as a "resize" of the element). */
      match();
      requestAnimationFrame(function () { requestAnimationFrame(match); });
      window.addEventListener("load", match);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(match);
      [200, 600, 1500].forEach(function (ms) { setTimeout(match, ms); });
      window.addEventListener("resize", match);
      if ("ResizeObserver" in window) {
        new ResizeObserver(match).observe(cardInner);
        new ResizeObserver(match).observe(media);
      }
    });
  }

  /* ------------------------------------------------------------- header */
  /* Publish the header's real height (incl. the festive marquee strip) so the home hero image starts exactly under it on phones/tablets. */
  function initHeaderHeight() {
    var header = $("[data-header]");
    if (!header) return;
    function set() {
      if (header.classList.contains("is-pinned")) return;
      document.documentElement.style.setProperty("--hdr-h", Math.round(header.getBoundingClientRect().height) + "px");
    }
    set();
    window.addEventListener("load", set);
    window.addEventListener("resize", set);
    if (window.ResizeObserver) new ResizeObserver(set).observe(header);
  }

  function initHeader() {
    var header = $("[data-header]");
    if (!header) return;
    var hasHero = !!$("[data-page-hero]");
    if (!hasHero) header.classList.add("header--nohero");

    function threshold() {
      var hero = $("[data-page-hero]");
      if (!hero) return 60;
      var r = hero.getBoundingClientRect();
      return r.top + window.scrollY + hero.offsetHeight - 1;
    }
    var t = threshold();
    function onScroll() { header.classList.toggle("is-pinned", window.scrollY > t); }
    function onResize() { t = threshold(); onScroll(); }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });

    var drawer = $("[data-mobile-nav]");
    $$("[data-mobile-nav-open]").forEach(function (b) {
      b.addEventListener("click", function () {
        if (drawer) { drawer.classList.add("is-open"); document.body.style.overflow = "hidden"; }
      });
    });
    $$("[data-mobile-nav-close]").forEach(function (b) {
      b.addEventListener("click", function () {
        if (drawer) { drawer.classList.remove("is-open"); document.body.style.overflow = ""; }
      });
    });

    /* Nav items with a mega panel or dropdown: clicking the label toggles the
       panel (instead of navigating straight to its href), matching the
       hover-or-click pattern on vamas.in. Hover keeps working via CSS. */
    var panelItems = $$(".header__navitem").filter(function (item) {
      return $(".meganav", item) || $(".header__dropdown", item);
    });
    function closeAllPanels(except) {
      panelItems.forEach(function (item) {
        if (item !== except) item.classList.remove("is-open");
      });
    }
    panelItems.forEach(function (item) {
      var link = $(".header__link", item);
      if (!link) return;
      link.addEventListener("click", function (e) {
        /* Always opens — never toggles closed on a second click, matching
           the reference site: the only ways to close it are moving the
           cursor away or clicking outside. */
        e.preventDefault();
        closeAllPanels(item);
        item.classList.add("is-open");
      });
      /* A click only opens it — the mouse is already over the item at that
         point, so :hover is showing it too. Once the cursor actually leaves
         the item (trigger AND panel, since the panel is nested inside it in
         the DOM, hovering it doesn't count as "leaving"), drop the sticky
         class so it closes instead of staying open until an unrelated click
         elsewhere on the page. A short delay (cancelled if the pointer comes
         straight back) tolerates the real-world gap between the trigger link
         and the panel below it — without it, moving the mouse diagonally
         from the link down into the panel can clip a sliver of unrelated
         page in between and close the menu before it arrives. */
      /* Hover also sets is-open (not just click) so the panel is covered by
         the same sticky class + grace-timer as a click-open. Plain CSS
         :hover has no tolerance for the visual gap between the trigger link
         and the panel below it -- it unmatches the instant the cursor
         leaves the item's own box, closing the menu before it reaches the
         panel. Driving it through is-open instead means the panel stays
         open across that gap and only closes after the real mouseleave. */
      var closeTimer;
      item.addEventListener("mouseenter", function () {
        clearTimeout(closeTimer);
        item.classList.add("is-open");
      });
      item.addEventListener("mouseleave", function () {
        closeTimer = setTimeout(function () { item.classList.remove("is-open"); }, 200);
      });
    });
    document.addEventListener("click", function (e) {
      panelItems.forEach(function (item) {
        if (!item.contains(e.target)) item.classList.remove("is-open");
      });
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") closeAllPanels(null);
    });
  }

  /* ------------------------------------------------------------ marquee */
  function initMarquee() {
    $$("[data-marquee]").forEach(function (el) {
      var rail = $(".marquee__rail", el);
      var speed = parseFloat(el.getAttribute("data-marquee")) || 42;
      if (rail) rail.style.animationDuration = speed + "s";
    });
  }

  /* --------------------------------------------------------------- tabs */
  function initTabs() {
    $$("[data-tabs]").forEach(function (root) {
      var btns = $$("[data-tab-btn]", root);
      var panels = $$("[data-tab-panel]", root);
      btns.forEach(function (btn) {
        btn.addEventListener("click", function () {
          var key = btn.getAttribute("data-tab-btn");
          btns.forEach(function (b) { b.setAttribute("aria-selected", String(b === btn)); });
          panels.forEach(function (p) {
            var on = p.getAttribute("data-tab-panel") === key;
            p.hidden = !on;
            if (on) { p.classList.remove("is-in"); void p.offsetWidth; p.classList.add("is-in"); }
          });
          var label = $("[data-tab-label]", root);
          if (label) label.textContent = btn.textContent.trim();
          /* "Shop Now"-style CTA below the grid: no explicit link configured,
             so it should always point at whichever tab is currently showing
             (link AND label), not stay stuck on the first tab's collection
             while still reading "Shop New Arrivals" on every other tab. */
          var cta = $("[data-tab-cta]", root);
          var url = btn.getAttribute("data-tab-url");
          if (cta && url) cta.href = url;
          var ctaText = $("[data-tab-cta-text]", root);
          var ctaLabel = btn.getAttribute("data-tab-cta-label");
          if (ctaText && ctaLabel) ctaText.textContent = ctaLabel;
        });
      });
    });
  }

  /* --------------------------------------------------- cinematic hero */
  function initCineHero() {
    var root = $("[data-cinehero]");
    if (!root) return;
    var video = $("video", root);
    if (!video) return;

    if (reduced) {
      video.pause();
      return;
    }

    var p = video.play();
    if (p && p.then) p.catch(function () {});
  }

  /* ------------------------------------- Royal Simplicity card rail */
  function initDeck() {
    $$("[data-deck]").forEach(function (root) {
      var viewport = $(".deck__viewport", root);
      var rail = $(".deck__rail", root);
      var cards = $$(".deck__card", rail);
      if (!cards.length) return;
      var last = cards.length - 1;
      var setSize = parseInt(rail.dataset.setSize, 10) || cards.length;
      var sets = Math.round(cards.length / setSize);
      var loopable = sets >= 3 && setSize > 0;

      /* offsetLeft is pure layout (untouched by the active card's transform:
         scale), so — unlike getBoundingClientRect — it gives a step size that
         stays correct no matter which cards are currently scaled up/down. */
      function step() {
        if (cards.length < 2) return cards[0].offsetWidth;
        return cards[1].offsetLeft - cards[0].offsetLeft;
      }
      function center(i) { return cards[i].offsetLeft + cards[i].offsetWidth / 2; }
      function setActive(i) {
        cards.forEach(function (c, idx) { c.classList.toggle("is-active", idx === i); });
        root.dataset.active = i;
      }
      function scrollToIndex(i, smooth) {
        viewport.scrollTo({ left: center(i) - viewport.clientWidth / 2, behavior: smooth && !reduced ? "smooth" : "auto" });
      }

      /* Which card is "active" is decided ONLY once scrolling has settled
         (debounced below) by picking whichever card's centre is nearest the
         viewport's centre — never reactively while a scroll is in flight.
         An IntersectionObserver-based approach (tried earlier) fires
         continuously as ratios cross a threshold, and during a scroll two
         neighbouring cards can straddle that threshold back and forth,
         each crossing toggling is-active (and its scale/opacity
         transition) repeatedly — that rapid toggling was the card "shake".
         Settling once, after the fact, makes exactly one clean transition
         happen per scroll. */
      function nearestIndex() {
        var target = viewport.scrollLeft + viewport.clientWidth / 2;
        var best = 0, bestDist = Infinity;
        cards.forEach(function (c, idx) {
          var d = Math.abs(center(idx) - target);
          if (d < bestDist) { bestDist = d; best = idx; }
        });
        return best;
      }

      var manual = false;
      var manualTimer;
      function releaseManual() { manual = false; clearTimeout(manualTimer); }
      function goTo(i, smooth) {
        var idx = clamp(i, 0, last);
        manual = true;
        setActive(idx);
        scrollToIndex(idx, smooth);
        clearTimeout(manualTimer);
        manualTimer = setTimeout(releaseManual, 1200);
      }

      var startIdx = loopable ? setSize + Math.floor(setSize / 2) : 0;
      goTo(startIdx, false);

      var jumping = false;
      function settle() {
        if (jumping) return;
        var a = parseInt(root.dataset.active || 0, 10);
        if (loopable && (a < setSize || a >= setSize * 2)) {
          jumping = true;
          var delta = a < setSize ? setSize : -setSize;
          viewport.scrollLeft += delta * step();
          setActive(a + delta);
          requestAnimationFrame(function () { jumping = false; });
          return;
        }
        if (manual) return;
        setActive(nearestIndex());
      }
      var scrollTimer;
      viewport.addEventListener("scroll", function () {
        if (jumping) return;
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(settle, 120);
      }, { passive: true });

      cards.forEach(function (c, i) {
        if (c.hasAttribute("aria-hidden")) return;
        c.addEventListener("click", function () { goTo(i, true); });
      });
      var prev = $("[data-deck-prev]", root), next = $("[data-deck-next]", root);
      if (prev) prev.addEventListener("click", function () {
        var a = parseInt(root.dataset.active || 0, 10); goTo(a - 1, true);
      });
      if (next) next.addEventListener("click", function () {
        var a = parseInt(root.dataset.active || 0, 10); goTo(a + 1, true);
      });

      /* Opt-in autoplay -- only for a rail carrying data-autoscroll (its
         value is the delay in ms), so Her Royal Simplicity's own rail is
         unaffected unless it opts in too. Pauses on hover/touch so it
         never fights a shopper mid-swipe. */
      var autoDelay = parseInt(root.dataset.autoscroll, 10);
      if (autoDelay > 0 && !reduced) {
        var autoTimer;
        function auto() {
          var a = parseInt(root.dataset.active || 0, 10);
          goTo(a + 1, true);
        }
        function stopAuto() { clearInterval(autoTimer); }
        function startAuto() { stopAuto(); autoTimer = setInterval(auto, autoDelay); }
        startAuto();
        viewport.addEventListener("mouseenter", stopAuto);
        viewport.addEventListener("mouseleave", startAuto);
        viewport.addEventListener("touchstart", stopAuto, { passive: true });
        viewport.addEventListener("touchend", function () { setTimeout(startAuto, 2000); }, { passive: true });
      }
    });
  }

  /* ------------------------------------------- Journal showcase 3D arc */
  function initArc() {
    $$("[data-arc]").forEach(function (root) {
      var cards = $$(".arc__card", root);
      var n = cards.length;
      if (!n) return;
      var DEPTH = 160, ANGLE = 8, STEP_VW = 40, STEP_MAX = 480;
      var stepPx = Math.min(window.innerWidth * (STEP_VW / 100), STEP_MAX);
      window.addEventListener("resize", function () {
        stepPx = Math.min(window.innerWidth * (STEP_VW / 100), STEP_MAX);
      }, { passive: true });

      function ringOffset(i, p) { var raw = i - p; return raw - n * Math.round(raw / n); }
      function update() {
        var rect = root.getBoundingClientRect();
        var travel = root.offsetHeight - window.innerHeight;
        var prog = travel > 0 ? clamp(-rect.top / travel, 0, 1) : 0;
        var pos = prog * n;
        var active = ((Math.round(pos) % n) + n) % n;
        root.dataset.active = active;
        cards.forEach(function (card, i) {
          var o = ringOffset(i, pos);
          var scale = 1 - Math.min(Math.abs(o), 2) * 0.16;
          var opacity = Math.max(0, 1 - Math.min(Math.abs(o), 2.4) * 0.42);
          card.style.transform = "translateX(" + (o * stepPx) + "px) translateZ(" + (-Math.abs(o) * DEPTH) + "px) rotateY(" + (o * -ANGLE) + "deg) scale(" + scale + ")";
          card.style.opacity = opacity;
          card.style.zIndex = String(100 - Math.round(Math.abs(o) * 10));
          var link = $("a", card);
          if (link) { link.tabIndex = i === active ? 0 : -1; }
        });
      }
      update();
      window.addEventListener("scroll", update, { passive: true });
      window.addEventListener("resize", update, { passive: true });

      function goTo(i) {
        var wrapped = ((i % n) + n) % n;
        var travel = root.offsetHeight - window.innerHeight;
        window.scrollTo({ top: root.offsetTop + (wrapped / n) * travel, behavior: reduced ? "auto" : "smooth" });
      }
      var prev = $("[data-arc-prev]", root), next = $("[data-arc-next]", root);
      if (prev) prev.addEventListener("click", function () { goTo(parseInt(root.dataset.active || 0, 10) - 1); });
      if (next) next.addEventListener("click", function () { goTo(parseInt(root.dataset.active || 0, 10) + 1); });
    });
  }

  /* ------------------------------------------------------- flip cards */
  function initFlip() {
    $$(".flipcard").forEach(function (card) {
      card.addEventListener("click", function (e) {
        if (e.target.closest("a")) return;
        card.classList.toggle("is-flipped");
      });
      card.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); card.classList.toggle("is-flipped"); }
      });
    });
  }

  /* ------------------------------------------------------- FAQ / accordion */
  function initDisclosures() {
    $$("[data-faq-row]").forEach(function (row) {
      var btn = $("[data-faq-btn]", row);
      if (!btn) return;
      function open() { row.classList.add("is-open"); btn.setAttribute("aria-expanded", "true"); }
      function close() { row.classList.remove("is-open"); btn.setAttribute("aria-expanded", "false"); }
      row.addEventListener("mouseenter", open);
      row.addEventListener("mouseleave", close);
      btn.addEventListener("click", function () {
        row.classList.contains("is-open") ? close() : open();
      });
      btn.addEventListener("focus", open);
    });
    $$("[data-accordion-row]").forEach(function (row) {
      var btn = $("[data-accordion-btn]", row);
      if (!btn) return;
      btn.addEventListener("click", function () {
        var on = row.classList.toggle("is-open");
        btn.setAttribute("aria-expanded", String(on));
      });
    });
  }

  /* --------------------------------------------------- drag rails (x) */
  function initDragRails() {
    $$("[data-drag-rail]").forEach(function (rail) {
      var down = false, startX = 0, startScroll = 0, moved = false;
      var vp = rail.parentElement;
      vp.style.overflowX = "auto";
      rail.classList.add("drag-rail");
      rail.addEventListener("pointerdown", function (e) {
        down = true; moved = false; startX = e.clientX; startScroll = vp.scrollLeft;
        rail.classList.add("is-dragging");
      });
      window.addEventListener("pointerup", function () {
        down = false; rail.classList.remove("is-dragging");
        setTimeout(function () { moved = false; }, 0);
      });
      window.addEventListener("pointermove", function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        vp.scrollLeft = startScroll - dx;
      });
      rail.addEventListener("click", function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
    });
  }

  /* ---------------------------------------------------------- kavach */
  function initKavach() {
    $$("[data-kavach]").forEach(function (root) {
      var viewport = $("[data-kavach-viewport]", root);
      var rail = $("[data-kavach-rail]", root);
      var dotsWrap = $("[data-kavach-dots]", root);
      var picks = $$(".kavach__pick", rail);
      if (picks.length < 2) return;

      /* How many whole circles actually fit is measured from real layout
         (stride = one pick's width + gap) rather than assumed, and pages
         are scrolled by exactly pageSize * stride — never by clientWidth,
         which is rarely an exact multiple of the circle size and would
         leave a sliver of the next one showing. */
      var stride = picks[1].offsetLeft - picks[0].offsetLeft;
      /* Never show more than 6 at once, but still adapt down (never up) for
         a viewport too narrow to fit 6 -- capped either way at exactly
         pageSize * stride so no partial circle is ever in the box. */
      var fitCount = Math.max(1, Math.floor(viewport.clientWidth / stride));
      var pageSize = Math.min(6, fitCount);
      var pages = Math.ceil(picks.length / pageSize);
      if (pages < 2) { dotsWrap.innerHTML = ""; return; }
      var pageWidth = pageSize * stride;
      viewport.style.maxWidth = pageWidth + "px";
      viewport.style.marginInline = "auto";

      dotsWrap.innerHTML = "";
      var dots = [];
      for (var i = 0; i < pages; i++) {
        var dot = document.createElement("button");
        dot.type = "button";
        dot.className = "kavach__dot" + (i === 0 ? " is-active" : "");
        var from = i * pageSize + 1, to = Math.min((i + 1) * pageSize, picks.length);
        dot.setAttribute("aria-label", "Show picks " + from + " to " + to);
        (function (idx) { dot.addEventListener("click", function () { goTo(idx, true); }); })(i);
        dotsWrap.appendChild(dot);
        dots.push(dot);
      }
      function setActive(i) {
        dots.forEach(function (d, j) { d.classList.toggle("is-active", j === i); });
      }
      function goTo(i, smooth) {
        viewport.scrollTo({ left: i * pageWidth, behavior: smooth && !reduced ? "smooth" : "auto" });
        setActive(i);
      }
      var scrollTimer;
      viewport.addEventListener("scroll", function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          var page = clamp(Math.round(viewport.scrollLeft / pageWidth), 0, pages - 1);
          setActive(page);
        }, 120);
      }, { passive: true });
    });
  }

  /* ------------------------------------------- Effortless Elegance swap */
  function initElegance() {
    $$("[data-elegance]").forEach(function (root) {
      var thumbs = $$("[data-eleg-thumb]", root);
      var previews = $$("[data-eleg-preview]", root);
      thumbs.forEach(function (t, i) {
        function activate() {
          thumbs.forEach(function (x, j) { x.classList.toggle("is-active", i === j); });
          previews.forEach(function (p, j) { p.classList.toggle("is-active", i === j); });
        }
        t.addEventListener("mouseenter", activate);
        t.addEventListener("focus", activate);
      });
    });
  }

  /* -------------------------------------------------- stone explorer */
  function initStones() {
    $$("[data-stones]").forEach(function (root) {
      var thumbs = $$("[data-stone-thumb]", root);
      var panels = $$("[data-stone-panel]", root);
      thumbs.forEach(function (t) {
        function activate() {
          var h = t.getAttribute("data-stone-thumb");
          thumbs.forEach(function (x) {
            var on = x === t;
            x.classList.toggle("is-active", on);
            x.setAttribute("aria-pressed", String(on));
          });
          panels.forEach(function (p) { p.classList.toggle("is-active", p.getAttribute("data-stone-panel") === h); });
        }
        t.addEventListener("mouseenter", activate);
        t.addEventListener("focus", activate);
        t.addEventListener("click", activate);
      });
    });
  }

  /* --------------------------------------------------- product gallery */
  function initGallery() {
    $$("[data-gallery]").forEach(function (root) {
      var thumbs = $$("[data-gallery-thumb]", root);
      var slides = $$("[data-gallery-slide]", root);
      function show(i) {
        slides.forEach(function (s, j) { s.classList.toggle("is-active", i === j); });
        thumbs.forEach(function (t, j) { t.classList.toggle("is-active", i === j); });
        root.dataset.active = i;
      }
      thumbs.forEach(function (t, i) { t.addEventListener("click", function () { show(i); }); });
      show(0);

      /* Swipe / drag / keyboard through the slides. Thumbnails alone are a poor
         target on mobile, and horizontal drag is what a product gallery implies.
         Only acts on a mostly-horizontal gesture so vertical page scrolling and
         the sticky media column are never hijacked. */
      if (slides.length > 1) {
        var stage = $("[data-gallery-stage]", root) || $(".gallery__stage", root);
        if (stage) {
          var down = false, sx = 0, sy = 0, locked = false;
          var step = function (dir) {
            var i = parseInt(root.dataset.active || 0, 10) + dir;
            show(clamp(i, 0, slides.length - 1));
          };
          stage.addEventListener("pointerdown", function (e) {
            if (e.pointerType === "mouse" && e.button !== 0) return;
            down = true; locked = false; sx = e.clientX; sy = e.clientY;
          });
          stage.addEventListener("pointermove", function (e) {
            if (!down || locked) return;
            var dx = e.clientX - sx, dy = e.clientY - sy;
            if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
            locked = true; down = false;
            step(dx < 0 ? 1 : -1);
          });
          window.addEventListener("pointerup", function () { down = false; });
          window.addEventListener("pointercancel", function () { down = false; });

          stage.setAttribute("tabindex", "0");
          stage.addEventListener("keydown", function (e) {
            if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
            else if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
          });
        }
      }

      var open3d = $("[data-gallery-3d]", root);
      var modal = $("[data-tilt-modal]");
      if (open3d && modal) {
        open3d.addEventListener("click", function () {
          var i = parseInt(root.dataset.active || 0, 10);
          var src = slides[i] ? $("img", slides[i]).currentSrc || $("img", slides[i]).src : "";
          var img = $("img", modal);
          if (img) img.src = src;
          openModal(modal);
        });
      }
    });

    // Click-to-zoom interaction: click toggles a magnified view that pans
    // to follow the cursor, instead of the old 3D tilt/rotate effect.
    var stage = $(".tilt-stage");
    if (stage) {
      var inner = $(".tilt-stage__inner", stage);
      var zoomed = false;
      var ZOOM = 2.2;
      function pan(e) {
        if (!zoomed) return;
        var r = stage.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        inner.style.transform = "scale(" + ZOOM + ") translate(" + (px * -100 / ZOOM) + "%, " + (py * -100 / ZOOM) + "%)";
      }
      stage.addEventListener("mousemove", pan);
      stage.addEventListener("mouseleave", function () {
        if (!zoomed) inner.style.transform = "scale(1)";
      });
      stage.addEventListener("click", function (e) {
        e.stopPropagation();
        zoomed = !zoomed;
        stage.classList.toggle("is-zoomed", zoomed);
        inner.style.transform = zoomed ? "scale(" + ZOOM + ")" : "scale(1)";
      });
    }
  }

  /* -------------------------------------------------------- modals */
  function openModal(m) { m.classList.add("is-open"); document.body.style.overflow = "hidden"; }
  function closeModal(m) { m.classList.remove("is-open"); document.body.style.overflow = ""; }
  function initModals() {
    $$("[data-modal]").forEach(function (m) {
      $$("[data-modal-close], .modal__scrim, .note-modal__scrim", m).forEach(function (b) {
        b.addEventListener("click", function () { closeModal(m); });
      });
    });
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape") return;
      $$("[data-modal].is-open, .cart-drawer.is-open, .search-drawer.is-open, .mobile-nav.is-open, .shop__drawer.is-open, .coin-modal.is-open").forEach(function (m) {
        m.classList.remove("is-open");
      });
      document.body.style.overflow = "";
    });
  }

  /* ----------------------------------------------------- quick view */
  function initQuickView() {
    var modal = $("[data-quickview]");
    if (!modal) return;
    document.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-quickview-open]");
      if (!btn) return;
      e.preventDefault(); e.stopPropagation();
      var url = btn.getAttribute("data-quickview-open");
      var body = $("[data-quickview-body]", modal);
      body.innerHTML = '<p class="lede" style="padding:2rem">Loading…</p>';
      openModal(modal);
      fetch(url + (url.indexOf("?") > -1 ? "&" : "?") + "view=quickview", { headers: { "X-Requested-With": "fetch" } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var frag = doc.querySelector("[data-quickview-content]");
          body.innerHTML = frag ? frag.innerHTML : html;
          initReveal(body); initSplit(body); initGallery();
        })
        .catch(function () { body.innerHTML = '<p class="lede" style="padding:2rem">Could not load this piece.</p>'; });
    });
  }

  /* --------------------------------------------------------- wishlist */
  var WISH_KEY = "sp_wishlist";
  function readStore(key) {
    try { var v = JSON.parse(localStorage.getItem(key) || "[]"); return Array.isArray(v) ? v : []; }
    catch (e) { return []; }
  }
  function writeStore(key, v) { try { localStorage.setItem(key, JSON.stringify(v)); } catch (e) {} }
  function initWishlist() {
    var list = readStore(WISH_KEY);
    $$("[data-wishlist]").forEach(function (btn) {
      if (btn.dataset.wishBound) return; // avoid stacking duplicate listeners on repeat init calls
      btn.dataset.wishBound = "1";
      var h = btn.getAttribute("data-wishlist");
      btn.setAttribute("aria-pressed", String(list.indexOf(h) > -1));
      btn.addEventListener("click", function (e) {
        e.preventDefault(); e.stopPropagation();
        var cur = readStore(WISH_KEY);
        var i = cur.indexOf(h);
        if (i > -1) cur.splice(i, 1); else cur.push(h);
        writeStore(WISH_KEY, cur);
        btn.setAttribute("aria-pressed", String(i === -1));
      });
    });
  }

  /* ------------------------------------------------------ wishlist page */
  function initWishlistPage() {
    var grid = $("[data-wishlist-grid]");
    if (!grid) return;
    var empty = $("[data-wishlist-empty]");
    var handles = readStore(WISH_KEY);
    if (!handles.length) { if (empty) empty.hidden = false; return; }
    var done = 0;
    function settle() {
      done++;
      if (done === handles.length && !grid.children.length && empty) empty.hidden = false;
    }
    handles.forEach(function (h) {
      fetch("/products/" + h + "?view=card")
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (html) {
          if (html) {
            var wrap = document.createElement("div");
            wrap.innerHTML = html.trim();
            var card = wrap.firstElementChild;
            if (card) grid.appendChild(card);
          }
          settle();
        })
        .catch(settle);
    });
    // Unwishing a card while on this page should drop it from view immediately,
    // rather than requiring a reload — delegate so it also covers cards
    // appended above after the initial fetches resolve.
    grid.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-wishlist]");
      if (!btn) return;
      requestAnimationFrame(function () {
        if (btn.getAttribute("aria-pressed") === "false") {
          var card = btn.closest("[data-product-card]");
          if (card) card.remove();
          if (!grid.children.length && empty) empty.hidden = false;
        }
      });
    });
    initReveal(grid);
    initWishlist();
  }

  /* -------------------------------------------------- recently viewed */
  function initRecentlyViewed() {
    var KEY = "sp_recently_viewed", MAX = 12;
    var meta = $("[data-recently-viewed-current]");
    var holder = $("[data-recently-viewed]");
    var stored = readStore(KEY);
    if (holder) {
      var handles = stored.filter(function (h) { return !meta || h !== meta.getAttribute("data-recently-viewed-current"); }).slice(0, 5);
      if (handles.length) {
        holder.hidden = false;
        var grid = $("[data-recently-viewed-grid]", holder);
        handles.forEach(function (h) {
          fetch("/products/" + h + "?view=card")
            .then(function (r) { return r.ok ? r.text() : null; })
            .then(function (html) {
              if (!html) return;
              var wrap = document.createElement("div");
              wrap.innerHTML = html.trim();
              grid.appendChild(wrap.firstElementChild);
              initReveal(grid); initWishlist();
            }).catch(function () {});
        });
      }
    }
    if (meta) {
      var cur = meta.getAttribute("data-recently-viewed-current");
      var next = [cur].concat(stored.filter(function (h) { return h !== cur; })).slice(0, MAX);
      writeStore(KEY, next);
    }
  }

  /* ------------------------------------------------------ cart (AJAX) */
  function cartCountUpdate(count) {
    $$("[data-cart-count]").forEach(function (el) { el.textContent = count; });
  }
  function refreshDrawer() {
    var drawer = $("[data-cart-drawer]");
    if (!drawer) return Promise.resolve();
    return fetch("/?section_id=cart-drawer")
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var fresh = doc.querySelector("[data-cart-drawer-inner]");
        var target = $("[data-cart-drawer-inner]", drawer);
        if (fresh && target) target.innerHTML = fresh.innerHTML;
        var c = doc.querySelector("[data-cart-count]");
        if (c) cartCountUpdate(c.textContent.trim());
        bindCartLines();
        bindCartClose(drawer);
        // celebration is triggered explicitly by the add-to-cart handler
        // below, not here -- this same refresh also runs every time the
        // shopper just opens the drawer to look at their cart, which isn't
        // a purchase-crossing-the-threshold moment.
      });
  }
  /* One delegated listener for every quantity / remove button, in the cart
     drawer and on the cart page, so it keeps working however the markup is
     re-rendered. data-line is the cart line's stable key. */
  var cartChanging = false;
  function cartChange(el, qty) {
    function post(body) {
      return fetch("/cart/change.js", {
        method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(body)
      });
    }
    /* Work out the line from where the button sits on screen (row N of the
       list), then look up that line's real key in the live cart -- this does
       not depend on what data-line holds, so stale or old markup still works. */
    var rows = $$(".cart-line, .cart-table tbody tr");
    var row = el.closest(".cart-line, .cart-table tbody tr");
    var idx = row ? rows.indexOf(row) : -1;
    var attr = el.getAttribute("data-line") || "";
    return fetch("/cart.js").then(function (c) { return c.json(); }).then(function (cart) {
      var item = null;
      if (attr.indexOf(":") > -1) item = cart.items.filter(function (it) { return it.key === attr; })[0];
      if (!item && idx > -1) item = cart.items[idx];
      if (!item && /^\d+$/.test(attr)) item = cart.items[Number(attr) - 1];
      if (!item) return { ok: false, json: function () { return Promise.resolve({ description: "That item is no longer in your bag." }); } };
      return post({ id: item.key, quantity: qty });
    }).then(function (r) {
      if (r.ok) return null;
      return r.json().then(function (j) { return (j && (j.description || j.message)) || "Could not update your bag."; }, function () { return "Could not update your bag."; });
    });
  }
  function bindCartLines() {
    if (bindCartLines.done) return;
    bindCartLines.done = true;
    document.addEventListener("click", function (e) {
      var el = e.target.closest && e.target.closest("[data-cart-change]");
      if (!el) return;
      e.preventDefault();
      if (cartChanging) return;
      var qty = Number(el.getAttribute("data-cart-change"));
      if (isNaN(qty)) return;
      cartChanging = true;
      cartChange(el, qty).then(function (msg) {
        if (msg) alert(msg);
        return refreshDrawer().then(checkCoinCelebration).catch(function () {});
      }).catch(function () {}).then(function () {
        cartChanging = false;
        if (document.body.classList.contains("template-cart")) location.reload();
      });
    });
  }
  function bindCartClose(drawer) {
    // refreshDrawer() replaces [data-cart-drawer-inner]'s whole innerHTML
    // (to pull fresh totals) every time the cart opens, which destroys and
    // recreates the header's close button -- so this has to be re-run after
    // every refresh, not just once at boot, or the new button has no listener.
    $$("[data-cart-close], .cart-drawer__scrim", drawer).forEach(function (b) {
      if (b.dataset.closeBound) return;
      b.dataset.closeBound = "1";
      b.addEventListener("click", function () { drawer.classList.remove("is-open"); document.body.style.overflow = ""; });
    });
  }
  function initCart() {
    var drawer = $("[data-cart-drawer]");
    $$("[data-cart-open]").forEach(function (b) {
      b.addEventListener("click", function (e) {
        if (!drawer) return;
        e.preventDefault();
        drawer.classList.add("is-open");
        document.body.style.overflow = "hidden";
        refreshDrawer();
      });
    });
    if (drawer) bindCartClose(drawer);
    bindCartLines();

    document.addEventListener("submit", function (e) {
      var form = e.target.closest("form.product-form, [data-add-to-cart]");
      if (!form) return;
      e.preventDefault();
      // e.submitter is which of the form's (possibly several) submit buttons
      // was actually clicked -- Add to Cart vs. Buy Now both submit the same
      // form, so this is how we tell them apart.
      var submitter = e.submitter || null;
      var buyNow = !!(submitter && submitter.hasAttribute("data-buy-now"));
      var btn = submitter && submitter.type === "submit" ? submitter : $('[type="submit"]', form);
      var label = btn ? btn.textContent : "";
      if (btn) { btn.disabled = true; btn.textContent = buyNow ? "Redirecting…" : "Adding…"; }
      fetch("/cart/add.js", { method: "POST", body: new FormData(form) })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
        .then(function (res) {
          if (!res.ok) {
            if (btn) { btn.disabled = false; btn.textContent = label; }
            alert(res.json.description || res.json.message || "Could not add to bag.");
            return;
          }
          if (buyNow) {
            // Buy Now skips the drawer entirely and jumps to checkout, but
            // the coin still has to be added to the cart *before* that
            // navigation -- there's no later moment on the checkout page
            // where the theme's JS runs again to catch it.
            return refreshDrawer().then(checkCoinCelebration).then(function () {
              window.location.href = "/checkout";
            });
          }
          if (btn) { btn.disabled = false; btn.textContent = label; }
          if (drawer) { drawer.classList.add("is-open"); document.body.style.overflow = "hidden"; }
          return refreshDrawer().then(checkCoinCelebration);
        })
        .catch(function () {
          if (btn) { btn.disabled = false; btn.textContent = label; }
          if (buyNow) { form.submit(); return; }
          form.submit();
        });
    });
  }

  /* ------------------------------------------------- variant selection */
  function initVariants() {
    $$("[data-variant-picker]").forEach(function (root) {
      var data = JSON.parse($("[data-variant-json]", root).textContent);
      var form = root.closest("form.product-form") || $("form.product-form") || $("[data-add-to-cart]");
      var idInput = form ? $('[name="id"]', form) : null;
      var priceEl = $("[data-variant-price]");
      var wasEl = $("[data-variant-compare]");
      var btn = form ? ($('[data-label-add]', form) || $('[type="submit"]:not([data-buy-now])', form)) : null;
      var buyBtn = form ? $('[data-buy-now]', form) : null;

      function selected() {
        return $$('input[type="radio"]:checked', root).map(function (i) { return i.value; });
      }
      var optMedia = {};
      data.forEach(function (v) { if (v.media && v.options[0] && !optMedia[v.options[0]]) optMedia[v.options[0]] = v.media; });
      function update(changedVal) {
        var opts = selected();
        var match = null;
        for (var i = 0; i < data.length; i++) {
          var v = data[i];
          var ok = true;
          for (var j = 0; j < opts.length; j++) { if (v.options[j] !== opts[j]) { ok = false; break; } }
          if (ok) { match = v; break; }
        }
        if (!match) return;
        if (idInput) idInput.value = match.id;
        if (priceEl) priceEl.textContent = money(match.price);
        var skuEl = $("[data-variant-sku]");
        if (skuEl) { var skuVal = $("[data-variant-sku-val]", skuEl); if (skuVal) skuVal.textContent = match.sku || ""; skuEl.hidden = !match.sku; }
        if (wasEl) {
          if (false && match.compare_at_price > match.price) {
            wasEl.textContent = money(match.compare_at_price); wasEl.hidden = false;
          } else { wasEl.hidden = true; }
        }
        if (btn) {
          btn.disabled = !match.available;
          var lbl = match.available ? btn.getAttribute("data-label-add") : btn.getAttribute("data-label-sold");
          if (lbl) btn.textContent = lbl;
        }
        if (buyBtn) buyBtn.disabled = !match.available;
        var showMedia = (typeof changedVal === "string" && optMedia[changedVal]) || match.media;
        if (showMedia) {
          var gal = $("[data-gallery]");
          if (gal) {
            var gthumbs = $$("[data-gallery-thumb]", gal);
            for (var gi = 0; gi < gthumbs.length; gi++) {
              if (gthumbs[gi].getAttribute("data-media-id") === String(showMedia)) { gthumbs[gi].click(); break; }
            }
          }
        }
        if (history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set("variant", match.id);
          history.replaceState({}, "", url.toString());
        }
      }
      $$('input[type="radio"]', root).forEach(function (i) { i.addEventListener("change", function () { update(i.value); }); });
      update();
    });
  }

  /* ------------------------------------------------------ sort dropdown */
  function initSort() {
    $$("[data-sortmenu]").forEach(function (menu) {
      var btn = $("[data-sortmenu-btn]", menu);
      btn.addEventListener("click", function () { menu.classList.toggle("is-open"); });
      document.addEventListener("mousedown", function (e) {
        if (!menu.contains(e.target)) menu.classList.remove("is-open");
      });
    });
  }

  /* ------------------------------------------------------ shop filters */
  function initFilters() {
    var drawer = $("[data-filter-drawer]");
    $$("[data-filter-open]").forEach(function (b) {
      b.addEventListener("click", function () { if (drawer) drawer.classList.add("is-open"); });
    });
    $$("[data-filter-close], [data-filter-drawer] > .shop__drawer-scrim").forEach(function (b) {
      b.addEventListener("click", function () { if (drawer) drawer.classList.remove("is-open"); });
    });
    if (drawer) drawer.addEventListener("click", function (e) { if (e.target === drawer) drawer.classList.remove("is-open"); });

    $$("[data-filter-form]").forEach(function (form) {
      form.addEventListener("change", function (e) {
        /* Skip the auto-submit-on-change for the price sliders themselves —
           they submit on their own "release" handler below (after clamping
           min/max so a crossed pair never fires an inverted, zero-result
           filter), while every other control (checkboxes) still submits
           immediately as before. */
        if (e.target.getAttribute("data-range-role")) return;
        /* A checkbox can stand for several merged filter values (see
           data-also in snippets/facets.liquid) -- add the extras as hidden
           inputs so the whole group is applied, and clear any stale ones. */
        $$("input[data-extra]", form).forEach(function (n) { n.remove(); });
        $$("input[data-also]:checked", form).forEach(function (cb) {
          cb.getAttribute("data-also").split("~~").forEach(function (pair) {
            var i = pair.indexOf("=");
            if (i < 1) return;
            var h = document.createElement("input");
            h.type = "hidden"; h.name = pair.slice(0, i); h.value = pair.slice(i + 1);
            h.setAttribute("data-extra", "1");
            form.appendChild(h);
          });
        });
        form.submit();
      });

      var minRange = $('[data-range-role="min"]', form);
      var maxRange = $('[data-range-role="max"]', form);
      var minOut = $('[data-range-output="min"]', form);
      var maxOut = $('[data-range-output="max"]', form);
      var fill = $('[data-price-fill]', form);

      function fmt(v) { return "₹" + Number(v).toLocaleString("en-IN"); }

      if (minRange && maxRange) {
        var lo = Number(minRange.min), hi = Number(minRange.max);
        var span = hi - lo || 1;
        function paint() {
          if (minOut) minOut.textContent = fmt(minRange.value);
          if (maxOut) maxOut.textContent = fmt(maxRange.value);
          if (fill) {
            var l = (Number(minRange.value) - lo) / span * 100;
            var r = (Number(maxRange.value) - lo) / span * 100;
            fill.style.left = l + "%";
            fill.style.width = (r - l) + "%";
          }
        }
        function submitRange() { form.submit(); }
        minRange.addEventListener("input", function () {
          if (Number(minRange.value) > Number(maxRange.value)) maxRange.value = minRange.value;
          paint();
        });
        maxRange.addEventListener("input", function () {
          if (Number(maxRange.value) < Number(minRange.value)) minRange.value = maxRange.value;
          paint();
        });
        minRange.addEventListener("change", submitRange);
        maxRange.addEventListener("change", submitRange);
        paint();
      }
    });
  }

  /* ------------------------------------------------------ note modal */
  function initNotes() {
    var modal = $("[data-note-modal]");
    if (!modal) return;
    $$("[data-note-open]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var q = btn.getAttribute("data-note-quote");
        var a = btn.getAttribute("data-note-by");
        var img = btn.getAttribute("data-note-image");
        var productUrl = btn.getAttribute("data-note-product-url");
        var productTitle = btn.getAttribute("data-note-product-title");
        $("[data-note-quote-out]", modal).textContent = "“" + q + "”";
        $("[data-note-by-out]", modal).textContent = a;
        var productOut = $("[data-note-product-out]", modal);
        if (productOut) {
          if (productUrl && productTitle) {
            productOut.href = productUrl;
            productOut.textContent = "— on our " + productTitle;
            productOut.hidden = false;
          } else {
            productOut.hidden = true;
          }
        }
        var photo = $("[data-note-photo-out]", modal);
        var mark = $("[data-note-mark-out]", modal);
        if (img) {
          $("img", photo).src = img;
          photo.hidden = false;
          if (mark) mark.hidden = true;
        } else {
          photo.hidden = true;
          if (mark) mark.hidden = false;
        }
        modal.classList.add("is-open");
        document.body.style.overflow = "hidden";
      });
    });
    $$("[data-note-close], .note-modal__scrim", modal).forEach(function (b) {
      b.addEventListener("click", function () { modal.classList.remove("is-open"); document.body.style.overflow = ""; });
    });
  }

  /* -------------------------------------------- free silver coin offer */
  var COIN_TIER_KEY = "sp_coin_tier";
  /* gift coins: added when the order qualifies, removed again if it stops qualifying */
  /* The "Buy X get Y" automatic discount only zeroes the coin's price if
     it's already a line in the cart -- it never adds the coin itself.
     This adds exactly as many as the shopper has earned (topping up, not
     duplicating, if some are already there), so a real gift shows up
     instead of just a banner promising one. */
  function ensureCoinsInCart(variantId, targetQty) {
    if (!variantId) return Promise.resolve();
    return fetch("/cart.js")
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var line = cart.items.filter(function (i) { return i.variant_id === Number(variantId); })[0];
        var have = line ? line.quantity : 0;
        /* No longer qualifying (items removed): take the gift coins back out so the
           shopper is never left paying for coins that are only free above the threshold. */
        if (targetQty <= 0) {
          if (!line) return null;
          return fetch("/cart/change.js", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: line.key, quantity: 0 })
          });
        }
        if (have >= targetQty) return null;
        return fetch("/cart/add.js", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: targetQty - have }] })
        });
      });
  }
  function checkCoinCelebration() {
    var el = $("[data-coin-offer]");
    if (!el) return Promise.resolve();
    var coins = parseInt(el.getAttribute("data-coins"), 10) || 0;
    var variantId = el.getAttribute("data-coin-variant-id");
    var gramsEach = parseFloat(el.getAttribute("data-coin-grams")) || 5;
    var prev = 0;
    try { prev = parseInt(localStorage.getItem(COIN_TIER_KEY) || "0", 10) || 0; } catch (e) {}
    var justUnlocked = coins > prev;
    try { localStorage.setItem(COIN_TIER_KEY, String(coins)); } catch (e) {}
    return ensureCoinsInCart(variantId, coins).then(function (res) {
      if (justUnlocked) celebrateCoins(coins, gramsEach);
      return res ? refreshDrawer() : undefined;
    });
  }
  function celebrateCoins(coins, gramsEach) {
    var modal = $("[data-coin-modal]");
    if (!modal) return;
    var grams = coins * (gramsEach || 5);
    var text = $("[data-coin-modal-text]", modal);
    if (text) {
      var label = coins > 1 ? "silver coins" : "a silver coin";
      text.textContent = "You've received " + label + " worth " + grams +
        "g of 99.9% pure silver — absolutely FREE!";
    }
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
  }
  function initCoinOffer() {
    $$("[data-coin-close], .coin-modal__scrim").forEach(function (b) {
      if (b.dataset.coinBound) return;
      b.dataset.coinBound = "1";
      b.addEventListener("click", function () {
        var modal = $("[data-coin-modal]");
        if (!modal) return;
        modal.classList.remove("is-open");
        modal.setAttribute("aria-hidden", "true");
        document.body.style.overflow = "";
      });
    });
    // no boot-time checkCoinCelebration() here -- this ran on every full
    // page load (any page has the cart drawer, hence [data-coin-offer], in
    // its DOM), popping the celebration on plain navigation/browsing
    // whenever it happened to disagree with localStorage's stored tier.
    // The celebration now only fires from the add-to-cart handler itself,
    // right when a purchase actually crosses a ₹15k/₹30k tier.
  }

  /* --------------------------------------------------- reel/video modal */
  function initReels() {
    var modal = $("[data-reel-modal]");
    if (!modal) return;
    var video = $("video", modal);
    $$("[data-reel-open]").forEach(function (b) {
      b.addEventListener("click", function () {
        openModal(modal);
        if (video) { try { video.currentTime = 0; video.play(); } catch (e) {} }
      });
    });
    $$("[data-modal-close], .modal__scrim", modal).forEach(function (b) {
      b.addEventListener("click", function () { if (video) video.pause(); });
    });
  }

  /* -------------------------------------------------------- PIN check */
  function initPin() {
    var root = $("[data-pincheck]");
    if (!root) return;
    var input = $("input", root), btn = $("button", root), out = $("[data-pin-out]", root);
    var fallback = function (v) { return "Estimated delivery to " + v + ": 5–7 business days. Dispatched within 2–3 days of order."; };
    function check() {
      var v = (input.value || "").replace(/\D/g, "");
      if (v.length !== 6) { out.textContent = "Enter a 6-digit PIN code."; return; }
      out.textContent = "Checking...";
      fetch("https://sequel-bridge.care-c4c.workers.dev/?pin=" + v)
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (d && d.ok && d.serviceable) {
            out.textContent = (d.deliveryBy ? "Delivery by " + d.deliveryBy : "Delivery available") + (d.city ? " to " + d.city : "") + " (" + v + ").";
          } else if (d && d.ok) {
            out.textContent = "Sorry, we cannot deliver to " + v + " yet. Please contact us.";
          } else { out.textContent = fallback(v); }
        })
        .catch(function () { out.textContent = fallback(v); });
    }
    btn.addEventListener("click", check);
    input.addEventListener("keydown", function (e) { if (e.key === "Enter") { e.preventDefault(); check(); } });
  }

  /* --------------------------------------------------------- gift note */
  function initGift() {
    $$("[data-gift-toggle]").forEach(function (cb) {
      var note = $("[data-gift-note]");
      cb.addEventListener("change", function () { if (note) note.hidden = !cb.checked; });
    });
  }


  /* Shopify's predictive search matches substrings/prefixes, not spelling
     -- "pandent" simply never matches "Pendant" server-side, no matter
     how many results are asked for. This corrects the query client-side
     against the store's own product vocabulary before searching, and
     merges results from both the typed and corrected query so a typo
     still surfaces every match a correct spelling would have. Shared by
     the search drawer and the full /search results page. */
  var SEARCH_VOCAB = [
    "pendant", "pendants", "earring", "earrings", "necklace", "necklaces",
    "bracelet", "bracelets", "ring", "rings", "anklet", "anklets",
    "chain", "chains", "bali", "jhumka", "jhumkas", "hoop", "hoops",
    "rakhi", "kavach", "silver", "sterling", "gemstone", "gemstones",
    "stone", "stones", "collection", "collections", "wedding", "festive",
    "ganpati", "moonlight", "lakshmi", "shakti", "tiger", "eye", "agate",
    "pyrite", "garnet", "ruby", "emerald", "amethyst", "quartz", "druzy",
    "peacock", "feather", "butterfly", "flower", "floral", "vintage",
    "designer", "handmade", "oxidised", "oxidized", "jewellery", "jewelry",
    "rose", "moonstone", "labradorite", "citrine", "coral", "kundan",
    "meenakari", "temple", "trishul", "ganesha", "lakshmi", "shiva",
    "hanuman", "nazar", "opal", "jasper", "topaz", "sapphire", "tanzanite",
    "larimar", "howlite", "malachite", "jade", "turquoise", "carnelian",
    "aventurine", "sunstone", "peridot", "onyx", "scolecite", "purpurite",
    "necklace", "mangalsutra", "nosepin", "chandbali", "choker", "cuff",
    "bangle", "bangles", "kada", "payal", "brooch", "studs", "stud",
    "dangle", "danglers", "drop", "drops", "minimalist", "minimal",
    "statement", "kids", "men", "women", "gift", "gifting",
    "huggies", "huggie", "semi", "precious", "delicate", "minimalistic",
    "green", "red", "blue", "black", "white", "pink", "purple", "brown",
    "gold", "golden", "multicolor", "multicolour", "colour", "color",
    "charan", "paduka", "padukas", "padukaa", "kuber", "nanak", "fish", "tortoise", "yantra", "coins"
  ];
  function searchEditDistance(a, b) {
    var m = a.length, n = b.length;
    var d = [];
    for (var i = 0; i <= m; i++) d[i] = [i];
    for (var j = 0; j <= n; j++) d[0][j] = j;
    for (i = 1; i <= m; i++) {
      for (j = 1; j <= n; j++) {
        d[i][j] = a[i - 1] === b[j - 1]
          ? d[i - 1][j - 1]
          : 1 + Math.min(d[i - 1][j], d[i][j - 1], d[i - 1][j - 1]);
      }
    }
    return d[m][n];
  }
  function correctSearchQuery(q) {
    return q.split(/\s+/).map(function (token) {
      var lc = token.toLowerCase();
      if (lc.length < 3) return token;
      var exact = SEARCH_VOCAB.indexOf(lc) !== -1;
      if (exact) return token;
      var best = null, bestDist = Infinity;
      SEARCH_VOCAB.forEach(function (word) {
        if (Math.abs(word.length - lc.length) > 2) return;
        var dist = searchEditDistance(lc, word);
        if (dist < bestDist) { bestDist = dist; best = word; }
      });
      var maxAllowed = lc.length <= 5 ? 1 : 2;
      return best && bestDist <= maxAllowed ? best : token;
    }).join(" ");
  }

  /* Shopify also matches words inside product descriptions, so a short
     query like "coin" drags in every earring whose copy mentions a "coin
     stud". When some products carry the query in their own title / type /
     tags, keep only those; if none do (typos, description-only terms) the
     list is left untouched. */
  /* A search that names a collection ("jhumka", "minimal") should land on
     that collection's products. Collection titles are matched word-prefix
     style; broad catch-all collections (>100 products) are ignored so
     "earring" / "silver" still run a normal search. */
  var SP_COLS = null;
  function loadCollections() {
    if (SP_COLS) return Promise.resolve(SP_COLS);
    try {
      var c = JSON.parse(sessionStorage.getItem("sp-cols") || "null");
      if (c && Date.now() - c.t < 36e5) { SP_COLS = c.l; return Promise.resolve(SP_COLS); }
    } catch (e) {}
    var list = [];
    function page(n) {
      return fetch("/collections.json?limit=250&page=" + n).then(function (r) { return r.json(); }).then(function (j) {
        var cs = (j && j.collections) || [];
        cs.forEach(function (x) { list.push({ handle: x.handle, title: x.title, count: x.products_count }); });
        return cs.length === 250 ? page(n + 1) : null;
      });
    }
    return page(1).then(function () {
      SP_COLS = list;
      try { sessionStorage.setItem("sp-cols", JSON.stringify({ t: Date.now(), l: list })); } catch (e) {}
      return list;
    }).catch(function () { return []; });
  }
  function collectionMatches(q) {
    var words = String(q || "").toLowerCase().replace(/"/g, "").split(/\s+/).filter(Boolean);
    if (!words.length || words.join("").length < 3) return Promise.resolve([]);
    return loadCollections().then(function (list) {
      return list.filter(function (c) {
        if (!c.count || c.count > 100) return false;
        var tw = c.title.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
        return words.every(function (w) { return tw.some(function (t) { return t.indexOf(w) === 0; }); });
      }).sort(function (a, b) { return b.count - a.count; });
    });
  }
  /* Intent keywords -> related collections and/or words found in product
     title / tags. t = phrases a shopper might type (typos tolerated),
     c = collection handles, w = words matched in title/tags, l = label. */
  var SP_ALL = "all";
  var SP_ALIASES = [
    { l: "Jhumka", t: ["jhumka", "jhumkas", "jhumki", "jumka"], c: ["jhumka-earrings", "chandini"], w: ["jhumka"] },
    { l: "Bali", t: ["bali", "balis", "baali"], c: ["bali-earring", "baali-collection"], w: ["bali"] },
    { l: "Minimal", t: ["minimal", "minimalistic", "minimalist", "minimal earring", "minimalistic earring", "delicate"], c: ["minimalistic-and-delicate", "minimalist-earrings", "minimalist-collection"], w: ["minimal"] },
    { l: "Everyday Wear", t: ["everyday", "every day", "everyday wear", "daily wear", "daily"], c: ["everyday-elegance", "gen-z-edit-everyday-silver"], w: [] },
    { l: "Office Wear", t: ["office", "officu", "office use", "office wear", "work wear"], c: ["office-muse"], w: [] },
    { l: "Designer", t: ["designer wear", "designer earring", "designer earrings", "designer"], c: ["designer-collection", "designer-statement-earrings"], w: ["designer"] },
    { l: "Heavy & Statement", t: ["heavy", "heavy earring", "statement", "statement earring", "big earring", "big earrings", "big"], c: ["designer-statement-earrings", "premium-collection", "spotlight-glam"], w: [] },
    { l: "Small & Delicate", t: ["small", "small earring", "small earrings", "tiny"], c: ["studs", "huggies-and-hoop", "minimalistic-and-delicate"], w: [] },
    { l: "Festive", t: ["festive", "festival", "festive wear", "festival wear", "diwali", "navratri"], c: ["festive-wedding-collection", "party-wear"], w: [] },
    { l: "Wedding", t: ["wedding", "bridal", "shaadi"], c: ["festive-wedding-collection"], w: [] },
    { l: "Party & Cocktail", t: ["cocktail", "kitty party", "kitty", "party", "party wear"], c: ["party-wear", "spotlight-glam"], w: [] },
    { l: "Glamour", t: ["spotlight", "glamour", "glamourous", "glam", "glamorous"], c: ["spotlight-glam"], w: [] },
    { l: "Traditional", t: ["traditional", "ethnic", "heritage"], c: ["virasat", "meenakari", "ethnic-bohemian-collection"], w: [] },
    { l: "Modern", t: ["modern", "contemporary"], c: ["minimalist-collection", "gen-z"], w: [] },
    { l: "Western & Indo-Western", t: ["western", "indo western", "indo-western", "indowestern"], c: ["quiet-luxury"], w: ["western"] },
    { l: "Classic", t: ["classic", "classy", "timeless"], c: ["classic-collection", "vintage-collection"], w: [] },
    { l: "Sterling Silver", t: ["sterling silver", "925 silver", "925 sterling silver"], c: [SP_ALL], w: [] },
    { l: "99.9 Silver Coins", t: ["99.9 silver", "99.9", "999 silver", "silver coin", "pure silver", "coin"], c: [], w: ["coin"] },
    { l: "Spiritual", t: ["spiritual", "spirituality", "healing stone", "healing stones", "healing", "positive energy stone", "positive gem stone", "positive crystal", "positive energy"], c: ["kavach", "shakti-kavach-protection-strength-grounding", "mann-shanti-calm-clarity-spiritual-balance", "lakshmi-ratna-wealth-prosperity-abundance"], w: [] },
    { l: "Gemstones", t: ["gems", "gem", "gemstone", "gemstones", "crystals", "crystal", "semi precious", "semi-precious", "stones", "stone"], c: ["gemstone-jewellery", "semi-precious-gemstone-earings", "the-rare-ratna-edit-collector-stones"], w: [] },
    { l: "Lucky & Abundance", t: ["vastu", "lucky", "luck", "abundance", "money attract", "money magnet", "money", "laxmi magnet", "laxmi abundance", "lucky stone", "lucky gem", "lucky crystal", "diwali laxmi lucky", "wealth"], c: ["lakshmi-ratna-wealth-prosperity-abundance"], w: ["pyrite", "laxmi", "lakshmi", "yantra"] },
    { l: "Laxmi Yantra", t: ["laxmi yantra", "lakshmi yantra", "laxmi", "lakshmi", "yantra"], c: ["lakshmi-ratna-wealth-prosperity-abundance"], w: ["laxmi", "lakshmi", "yantra"] },
    { l: "Zodiac & Birthstone", t: ["zodiac", "birth stone", "birthstone", "birth gem", "rashi"], c: ["gemstone-jewellery", "the-rare-ratna-edit-collector-stones"], w: [] },
    { l: "Nazar & Evil Eye", t: ["nazar", "evil eye", "nazaria", "drishti"], c: [], w: ["nazar", "evil eye", "drishti"] },
    { l: "Shri Ram", t: ["shri ram", "shree ram", "ram", "rama", "jai shri ram"], c: [], w: ["ram", "rama"] },
    { l: "Hanuman", t: ["hanuman", "hanuman ji", "panchmukhi", "panchmukhi hanuman", "bajrang"], c: ["sankat-mochan-ratna-protection-problem-solving"], w: ["hanuman", "panchmukhi"] },
    { l: "Shiva & Trinetra", t: ["shiva", "shiv", "shivji", "shiv eye", "trinetra", "trishul", "mahadev"], c: [], w: ["shiva", "shiv", "trinetra", "trishul", "mahadev"] },
    { l: "Phoenix", t: ["phoenix"], c: [], w: ["phoenix"] },
    { l: "Buddha & Peace", t: ["budha", "buddha", "peace", "calm"], c: ["mann-shanti-calm-clarity-spiritual-balance"], w: ["buddha", "budha"] },
    { l: "Lotus", t: ["lotus", "kamal"], c: [], w: ["lotus"] },
    { l: "Horse", t: ["horse", "lucky horse"], c: [], w: ["horse"] },
    { l: "Feather & Peacock", t: ["feather", "peacock", "peacock feather", "mor pankh"], c: [], w: ["feather", "peacock"] },
    { l: "Krishna & Radha", t: ["krishna", "krishn", "radha krishn", "radha krishna", "radha", "bansuri", "basuri", "flute"], c: [], w: ["krishna", "radha", "bansuri", "flute"] },
    { l: "Griffin", t: ["griffin", "dragon"], c: [], w: ["griffin", "dragon"] },
    { l: "Yin Yang", t: ["yin yan", "yin yang", "yinyang"], c: [], w: ["yin yang"] },
    { l: "Feng Shui & Lucky Charms", t: ["chinese luck", "feng shui", "fen shui", "fengshui", "koi", "tortoise"], c: [], w: ["koi", "fish", "yin yang", "horse", "tortoise"] }
  ];
  function normQ(q) { return String(q || "").toLowerCase().replace(/[^a-z0-9. ]+/g, " ").replace(/\s+/g, " ").trim(); }
  function aliasMatch(q) {
    var n = normQ(q);
    if (n.length < 3) return null;
    var best = null;
    SP_ALIASES.forEach(function (a) {
      a.t.forEach(function (term) {
        var d = n === term ? 0 : Math.abs(n.length - term.length) > 2 ? 99 : searchEditDistance(n, term);
        var max = term.length >= 8 ? 2 : term.length >= 5 ? 1 : 0;
        if (d <= max && (!best || d < best.d)) best = { a: a, d: d };
      });
    });
    return best ? best.a : null;
  }
  function hayHasWord(hay, words) {
    return words.some(function (w) { return new RegExp("\\b" + w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).test(hay || ""); });
  }

  /* Colour words -> the "Color: X" product tag they filter on. */
  var SP_COLOURS = { black: "Black", blue: "Blue", brown: "Brown", gold: "Gold", golden: "Gold",
    green: "Green", multicolor: "Multicolor", multicolour: "Multicolor", pink: "Pink",
    purple: "Purple", red: "Red", white: "White" };
  function colourMatch(q) {
    var w = String(q || "").toLowerCase().replace(/"/g, "").trim();
    return SP_COLOURS[w] ? { name: SP_COLOURS[w], tag: "Color: " + SP_COLOURS[w] } : null;
  }
  /* Words in a Color variant name that count as each filter colour. */
  var SP_COLOUR_WORDS = {
    Black: /black/, Blue: /blue|navy|turquoise|sapphire/, Brown: /brown/, Gold: /gold|yellow|amber/,
    Green: /green|emerald/, Multicolor: /multi/, Pink: /pink/, Purple: /purple|violet|amethyst/,
    Red: /\bred\b|ruby|maroon/, White: /white|crystal|clear|pearl/
  };
  /* A product sold in several colour variants (say Green and Blue): when the
     shopper is looking at one colour, return the variant of that colour so
     its card can link to it and show its photo. Null when there is none. */
  function colourVariant(product, colourName) {
    var re = SP_COLOUR_WORDS[colourName];
    if (!re || !product || !product.variants || product.variants.length < 2) return null;
    var idx = -1;
    (product.options || []).forEach(function (o, i) { if (/colou?r/i.test(o.name)) idx = i; });
    if (idx < 0) return null;
    var key = "option" + (idx + 1);
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      if (re.test(String(v[key] || "").toLowerCase())) return v;
    }
    return null;
  }
  function applyColourVariants(scope, colourNames) {
    if (!colourNames || !colourNames.length) return;
    loadCatalog().then(function (cat) {
      var byHandle = {};
      cat.forEach(function (p) { byHandle[p.handle] = p; });
      $$("[data-product-card]", scope).forEach(function (card) {
        var href = (card.getAttribute("href") || "").split("?")[0];
        var p = byHandle[href.split("/").pop()];
        if (!p) return;
        var v = null;
        for (var i = 0; i < colourNames.length && !v; i++) v = colourVariant(p, colourNames[i]);
        if (!v) return;
        card.setAttribute("href", href + "?variant=" + v.id);
        var src = v.featured_image && v.featured_image.src;
        var img = $(".card__img--main", card);
        if (src && img) { img.setAttribute("src", src + (src.indexOf("?") > -1 ? "&" : "?") + "width=800"); img.removeAttribute("srcset"); }
      });
    });
  }
  /* Collection / search pages filtered by a "Color: X" tag. */
  function initColourVariantCards() {
    var params = new URLSearchParams(location.search);
    var names = [];
    params.forEach(function (val, key) {
      if (key.indexOf("filter.p.tag") === 0 && /^color:/i.test(val)) names.push(val.replace(/^color:\s*/i, "").trim());
    });
    applyColourVariants(document, names);
  }
  var SP_CAT_P = null;
  function loadCatalog() {
    if (SP_CAT_P) return SP_CAT_P;
    var all = [];
    function page(n) {
      return fetch("/products.json?limit=250&page=" + n).then(function (r) { return r.json(); }).then(function (j) {
        var list = (j && j.products) || [];
        all = all.concat(list);
        return list.length === 250 ? page(n + 1) : all;
      });
    }
    SP_CAT_P = page(1).catch(function () { SP_CAT_P = null; return []; });
    return SP_CAT_P;
  }
  function cardProduct(p) {
    return { title: p.title, url: "/products/" + p.handle, image: p.images && p.images[0] ? p.images[0].src : "",
             _priceText: p.variants && p.variants[0] ? money(Math.round(parseFloat(p.variants[0].price) * 100)) : "" };
  }
  var SP_HAY = null, SP_HAY_P = null;
  /* handle -> lowercase "title type tags option-values" for the whole
     catalog, so colour words ("green") match products by their Color
     option / stone tags too, not just what suggest.json returns. */
  function loadHay() {
    if (SP_HAY) return Promise.resolve(SP_HAY);
    if (SP_HAY_P) return SP_HAY_P;
    try {
      var c = JSON.parse(sessionStorage.getItem("sp-hay") || "null");
      if (c && Date.now() - c.t < 36e5) { SP_HAY = c.m; return Promise.resolve(SP_HAY); }
    } catch (e) {}
    var map = {};
    function page(n) {
      return fetch("/products.json?limit=250&page=" + n).then(function (r) { return r.json(); }).then(function (j) {
        var list = (j && j.products) || [];
        list.forEach(function (x) {
          var opts = (x.options || []).map(function (o) { return (o.values || []).join(" "); }).join(" ");
          var tags = Array.isArray(x.tags) ? x.tags.join(" ") : (x.tags || "");
          map[x.handle] = (x.title + " " + (x.product_type || "") + " " + tags + " " + opts).toLowerCase();
        });
        return list.length === 250 ? page(n + 1) : null;
      });
    }
    SP_HAY_P = page(1).then(function () {
      SP_HAY = map;
      try { sessionStorage.setItem("sp-hay", JSON.stringify({ t: Date.now(), m: map })); } catch (e) {}
      return map;
    }).catch(function () { SP_HAY_P = null; return {}; });
    return SP_HAY_P;
  }
  function productHaystack(p) {
    if (SP_HAY && p.handle && SP_HAY[p.handle]) return SP_HAY[p.handle];
    var tags = Array.isArray(p.tags) ? p.tags.join(" ") : (p.tags || "");
    return ((p.title || "") + " " + (p.type || p.product_type || "") + " " + tags).toLowerCase();
  }
  function preferTitleMatches(products, q) {
    var words = String(q || "").toLowerCase().replace(/"/g, "").split(/\s+/).filter(Boolean);
    if (!words.length || !products || products.length < 2) return products;
    var hits = products.filter(function (p) {
      var h = productHaystack(p);
      return words.every(function (w) { return h.indexOf(w) !== -1; });
    });
    return hits.length ? hits : products;
  }

  /* ------------------------------------------------------ search drawer */
  function initSearchDrawer() {
    var drawer = $("[data-search-drawer]");
    if (!drawer) return;
    var input   = $("[data-search-input]", drawer);
    var results = $("[data-search-results]", drawer);
    var allLink = $("[data-search-all]", drawer);
    var hint    = results ? results.innerHTML : "";
    var base    = (window.SilverPlay && window.SilverPlay.routes && window.SilverPlay.routes.search) || "/search";
    var timer, lastQuery = "", controller;

    function open(e) {
      if (e) e.preventDefault();
      drawer.classList.add("is-open");
      drawer.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
      if (input) setTimeout(function () { input.focus(); }, 120);
    }
    function close() {
      drawer.classList.remove("is-open");
      drawer.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
    }

    $$("[data-search-open]").forEach(function (b) { b.addEventListener("click", open); });
    $$("[data-search-close], .search-drawer__scrim", drawer).forEach(function (b) {
      b.addEventListener("click", close);
    });

    function esc(t) {
      return String(t == null ? "" : t).replace(/[&<>"']/g, function (c) {
        return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
      });
    }
    function row(item, meta) {
      var img = item.featured_image && item.featured_image.url ? item.featured_image.url : (item.image || "");
      return '<a class="sresult" href="' + esc(item.url) + '">' +
        (img ? '<span class="sresult__img"><img src="' + esc(img) + '" alt="" loading="lazy"></span>' : "") +
        '<span><span class="sresult__title">' + esc(item.title) + "</span>" +
        (meta ? '<span class="sresult__meta">' + meta + "</span>" : "") +
        "</span></a>";
    }
    function group(label, items, metaFor) {
      if (!items || !items.length) return "";
      return '<div class="search-drawer__group"><p class="search-drawer__label">' + esc(label) + "</p>" +
             items.map(function (i) { return row(i, metaFor ? metaFor(i) : ""); }).join("") + "</div>";
    }

    /* Diwali hampers are variants of ONE product, so Shopify search can't show them
       one by one. A hamper whose name matches the query is put at the top of the
       results, linking straight to that hamper's own customise page; the generic
       "Diwali Hamper" product is dropped when hampers are shown. */
    var hamperList = null;
    function hamperData() {
      if (hamperList === null) {
        try { hamperList = JSON.parse($("[data-hamper-search]").textContent) || []; } catch (e) { hamperList = []; }
      }
      return hamperList;
    }
    function hamperHits(q) {
      var skip = { diwali: 1, hamper: 1, hampers: 1, the: 1, of: 1, gift: 1, gifts: 1 };
      var toks = String(q || "").toLowerCase().replace(/["'\-]/g, " ").split(/\s+/).filter(Boolean);
      var hasWord = toks.some(function (t) { return t === "hamper" || t === "hampers"; });
      var spec = toks.filter(function (t) { return t.length >= 2 && !skip[t]; });
      if (!spec.length && !hasWord) return [];
      return hamperData().filter(function (h) {
        return spec.every(function (t) { return h.kw.indexOf(t) !== -1; });
      }).map(function (h) { return { title: h.name, url: h.url, image: h.img, _priceText: h.price }; });
    }
    var hamperBusy = false;
    function addHamperRows() {
      if (hamperBusy || !results) return;
      try {
        var q = (input && input.value || "").trim();
        var hits = hamperHits(q);
        if (!hits.length || $("[data-hamper-group]", results)) return;
        hamperBusy = true;
        $$('a.sresult[href^="/products/diwali-hamper"]', results).forEach(function (a) {
          var g = a.parentNode; a.remove();
          if (g && g.classList && g.classList.contains("search-drawer__group") && !$(".sresult", g)) g.remove();
        });
        var hint2 = $(".search-drawer__hint", results); if (hint2) hint2.remove();
        results.insertAdjacentHTML("afterbegin",
          '<div data-hamper-group>' + group("Diwali hampers", hits, function (p) { return esc(p._priceText); }) + "</div>");
        if (allLink) { allLink.hidden = false; allLink.href = base + "?q=" + encodeURIComponent(q); }
      } catch (e) { /* never let this break search */ } finally { hamperBusy = false; }
    }
    if (results && typeof MutationObserver !== "undefined") {
      new MutationObserver(addHamperRows).observe(results, { childList: true });
    }

    var correctQuery = correctSearchQuery;

    function render(data, q) {
      loadHay().then(function () { doRender(data, q); });
    }
    function doRender(data, q) {
      var r = (data && data.resources && data.resources.results) || {};
      r = { products: preferTitleMatches(r.products, q), articles: r.articles, pages: r.pages };
      var html = group("Products", r.products, function (p) {
            /* Fallback-search results already carry a formatted price
               string (scraped from the rendered card); suggest.json's own
               results instead give a decimal string in the shop's main
               currency unit (e.g. "24999.00"), not cents — money() expects
               cents, so it has to be scaled up first or it divides an
               already-whole-rupee price by 100 again. */
            if (p._priceText) return esc(p._priceText);
            return p.price ? money(Math.round(parseFloat(p.price) * 100)) : "";
          }) +
          group("Journal", r.articles) +
          group("Pages", r.pages);
      if (!html) {
        results.innerHTML = '<p class="search-drawer__hint">No results for &ldquo;' + esc(q) + '&rdquo;.</p>';
        if (allLink) allLink.hidden = true;
        return;
      }
      results.innerHTML = html;
      if (allLink) allLink.hidden = false;
      if (allLink) allLink.href = base + "?q=" + encodeURIComponent(q);
    }

    function suggest(q, signal) {
      /* No "collection" type — collections shouldn't appear in search at
         all. limit=10 is Shopify's own hard ceiling for this endpoint (it
         cannot return more here); "View all results" below links through
         to the full /search page for the complete list. */
      var url = base + "/suggest.json?q=" + encodeURIComponent(q) +
                "&resources[type]=product,article,page&resources[limit]=10" +
                "&resources[options][unavailable_products]=last";
      return fetch(url, signal ? { signal: signal } : undefined).then(function (res) { return res.json(); }).then(function (data) {
        var r = data && data.resources && data.resources.results;
        if (r && r.products) r.products = r.products.filter(function (p) { return !/\/products\/free-gift-/.test(p.url || ""); });
        return data;
      });
    }
    function resultCount(data) {
      var r = (data && data.resources && data.resources.results) || {};
      return (r.products || []).length + (r.articles || []).length + (r.pages || []).length;
    }

    /* Shopify's lightweight suggest.json (predictive search) sometimes
       comes back completely empty for a real, single-word match that the
       full /search page finds fine — e.g. "Rose" alone returns 0 here even
       though "Sterling Silver Rose Quartz ... Pendant" is a real product
       and /search?q=Rose correctly finds it. suggest.json's relevance
       engine is a different (and evidently stricter) backend than the
       full search page's, not something this theme can tune directly, so
       when it comes up empty this falls back to the SAME backend the full
       page uses (a Section Rendering API fetch of the search results
       section) and parses the product cards out of the returned HTML. */
    var searchSectionId = null;
    function getSearchSectionId() {
      if (searchSectionId) return Promise.resolve(searchSectionId);
      return fetch(base + "?q=x")
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var m = html.match(/id="shopify-section-(template--\d+__main)"/);
          searchSectionId = m ? m[1] : null;
          return searchSectionId;
        })
        .catch(function () { return null; });
    }
    function fallbackFullSearch(q) {
      return getSearchSectionId().then(function (sectionId) {
        if (!sectionId) return null;
        return fetch(base + "?q=" + encodeURIComponent(q) + "&section_id=" + sectionId)
          .then(function (res) { return res.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, "text/html");
            var products = $$(".card[data-product-card]", doc).slice(0, 10).map(function (el) {
              var img = $(".card__img", el);
              var priceEl = $(".card__price span", el);
              return {
                title: (($(".card__title", el) || {}).textContent || "").trim(),
                url: el.getAttribute("href"),
                image: img ? img.getAttribute("src") : "",
                _priceText: priceEl ? priceEl.textContent.trim() : ""
              };
            });
            if (!products.length) return null;
            return { resources: { results: { products: products, articles: [], pages: [] } } };
          });
      }).catch(function () { return null; });
    }

    function showInDrawer(label, products, q) {
      /* the free gift coin is never shown as a result */
      products = (products || []).filter(function (p) { return !/\/products\/free-gift-/.test((p && p.url) || ""); });
      if (!products.length) return false;
      results.innerHTML = group(label, products.slice(0, 10), function (p) { return esc(p._priceText); });
      if (allLink) { allLink.hidden = false; allLink.href = base + "?q=" + encodeURIComponent(q); }
      return true;
    }
    function renderCollections(cols, q) {
      return Promise.all(cols.map(function (col) {
        return fetch("/collections/" + col.handle + "/products.json?limit=10").then(function (r) { return r.json(); })
          .then(function (j) { return (j && j.products) || []; }).catch(function () { return []; });
      })).then(function (lists) {
        var seen = {}, items = [];
        lists.forEach(function (l) { l.forEach(function (p) { if (!seen[p.handle]) { seen[p.handle] = 1; items.push(cardProduct(p)); } }); });
        return showInDrawer(cols.map(function (c) { return c.title; }).join(" + "), items, q);
      });
    }
    function renderAlias(a, q) {
      var colFetches = a.c.map(function (h) {
        var u = h === SP_ALL ? "/collections/all/products.json?limit=10" : "/collections/" + h + "/products.json?limit=10";
        return fetch(u).then(function (r) { return r.json(); }).then(function (j) { return (j && j.products) || []; }).catch(function () { return []; });
      });
      var catP = a.w.length ? Promise.all([loadCatalog(), loadHay()]) : Promise.resolve(null);
      return Promise.all([Promise.all(colFetches), catP]).then(function (res) {
        var seen = {}, items = [];
        function add(p) { if (!seen[p.handle]) { seen[p.handle] = 1; items.push(cardProduct(p)); } }
        res[0].forEach(function (list) { list.forEach(add); });
        if (res[1]) {
          var cat = res[1][0], hay = res[1][1];
          cat.forEach(function (p) { if (hayHasWord(hay[p.handle] || "", a.w)) add(p); });
        }
        return showInDrawer(a.l, items, q);
      }).catch(function () { return false; });
    }
    function renderColour(c, q) {
      return loadCatalog().then(function (cat) {
        var hits = cat.filter(function (p) {
          var tags = Array.isArray(p.tags) ? p.tags : String(p.tags || "").split(",");
          return tags.some(function (t) { return t.trim().toLowerCase() === c.tag.toLowerCase(); });
        }).map(function (p) {
          var item = cardProduct(p), v = colourVariant(p, c.name);
          if (v) {
            item.url = "/products/" + p.handle + "?variant=" + v.id;
            if (v.featured_image && v.featured_image.src) item.image = v.featured_image.src;
          }
          return item;
        });
        return showInDrawer(c.name, hits, q);
      }).catch(function () { return false; });
    }
    function run(q) {
      /* Misspellings are corrected first ("gren" -> green, "jumka" -> jhumka);
         a colour or collection name then lists its products right here. */
      var fixed = correctQuery(q);
      var colour = colourMatch(fixed);
      var alias = colour ? null : (aliasMatch(q) || aliasMatch(fixed));
      var chain = colour ? renderColour(colour, q) : alias ? renderAlias(alias, q) : collectionMatches(fixed).then(function (cols) {
        return cols.length ? renderCollections(cols, q) : false;
      });
      chain.then(function (ok) { if (!ok) runSearch(q); });
    }
    function runSearch(q) {
      if (controller) controller.abort();
      controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      var signal = controller ? controller.signal : undefined;
      var corrected = correctQuery(q);
      function withFallback(data) {
        if (resultCount(data) > 0) { render(data, q); return; }
        fallbackFullSearch(q).then(function (fallbackData) { render(fallbackData || data, q); });
      }
      if (corrected === q) {
        suggest(q, signal).then(withFallback).catch(handleError(q));
        return;
      }
      /* A corrected spelling is a confident match against the store's own
         vocabulary -- trust it on its own rather than merging in the raw
         typo'd query's results too. Shopify's own fuzzy matching on the
         uncorrected query (e.g. "wuartz") often pulls in unrelated products
         that just happen to share a few letters, which showed up as
         "random stones" mixed in after the real Rose Quartz matches. The
         raw query is only used as a fallback if the correction itself
         comes up empty. */
      suggest(corrected, signal)
        .then(function (correctedData) {
          if (resultCount(correctedData) > 0) { render(correctedData, q); return; }
          return suggest(q, signal).then(withFallback);
        })
        .catch(handleError(q));
    }
    function handleError(q) {
      return function (err) {
        if (err && err.name === "AbortError") return;
        // network or endpoint failure: fall back to the full search page
        results.innerHTML = '<p class="search-drawer__hint">' +
          '<a class="link-cta" href="' + base + "?q=" + encodeURIComponent(q) + '">Search for &ldquo;' +
          esc(q) + '&rdquo; &rarr;</a></p>';
        if (allLink) allLink.hidden = true;
      };
    }

    if (input) {
      input.addEventListener("input", function () {
        var q = input.value.trim();
        if (q === lastQuery) return;
        lastQuery = q;
        clearTimeout(timer);
        if (q.length < 2) {
          results.innerHTML = hint;
          if (allLink) allLink.hidden = true;
          return;
        }
        timer = setTimeout(function () { run(q); }, 220);
      });
    }
  }

  /* The full /search results page is plain server-rendered Liquid
     (search.results), so a misspelling that Shopify's own backend search
     can't stem to anything real comes back with a genuine zero-result
     page -- there's no client-side retry happening there at all, unlike
     the drawer above. When that happens and our own vocabulary comes up
     with a different spelling, offer it as a "Did you mean" link rather
     than silently redirecting, so the shopper still sees products instead
     of a dead end, without us overriding what they actually typed. */
  function initSearchResultsPage() {
    var input = $(".search-page__form input[name=q]");
    if (input) input.value = input.value.replace(/^"(.*)"$/, "$1");
    /* Shopify's loose matching pads a one-word search with weak matches
       (e.g. "coin" -> 176 results, mostly earrings). When the exact quoted
       word has hits of its own, show only those; otherwise leave the loose
       results alone so partial words like "ros" still work. */
    var params = new URLSearchParams(location.search);
    var term = (params.get("q") || "").trim();
    var rawTerm = term.replace(/"/g, "").trim();
    function exactRedirect() {
      if (/^[^\s"]{3,}$/.test(term) && !params.get("page")) {
        var sbase = (window.SilverPlay && window.SilverPlay.routes && window.SilverPlay.routes.search) || "/search";
        var quoted = sbase + "?q=" + encodeURIComponent('"' + term + '"') + "&options%5Bprefix%5D=last";
        fetch(quoted, { credentials: "same-origin" }).then(function (r) { return r.text(); }).then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          if (doc.querySelector("[data-product-card]")) location.replace(quoted); else reveal();
        }).catch(function () { reveal(); });
      } else { reveal(); }
    }
    /* A collection-named search ("jhumka") shows that collection's products
       right here on the search page (no redirect): its cards are fetched
       from the collection's own pages and swapped into the results grid. */
    var showingCollection = false;
    var wrapEl = $(".search-page__form") ? $(".search-page__form").parentNode : null;
    function reveal() { if (wrapEl) wrapEl.classList.remove("search-page--pending"); }
    setTimeout(reveal, 4000);
    function showListing(sources, colourNames) {
      var all = [], seen = {};
      function pull(src, n) {
        var url = src.url;
        return fetch(url + (url.indexOf("?") > -1 ? "&" : "?") + "page=" + n).then(function (r) { return r.text(); }).then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var found = $$(".grid-products [data-product-card]", doc);
          found.forEach(function (c) {
            var h = (c.getAttribute("href") || "").split("?")[0];
            if (seen[h]) return;
            if (src.words && !hayHasWord((src.hay || {})[h.split("/").pop()] || "", src.words)) return;
            seen[h] = 1; all.push(document.importNode(c, true));
          });
          return found.length && doc.querySelector(".pagination a[href*='page=" + (n + 1) + "']") && n < (url === "/collections/all" ? 12 : 4) ? pull(src, n + 1) : null;
        });
      }
      var label = (sources.label) || sources.map(function (x) { return x.label; }).join(" + ");
      return Promise.all(sources.map(function (x) { return pull(x, 1); })).then(function () {
        if (!all.length) return false;
        showingCollection = true;
        var wrap = wrapEl;
        $$(".grid-products, .pagination, .shop__empty", wrap).forEach(function (n) { n.remove(); });
        var cnt = $(".shop__count", wrap);
        if (!cnt) {
          cnt = document.createElement("p"); cnt.className = "shop__count mt-8";
          wrap.appendChild(cnt);
        }
        cnt.textContent = all.length + (all.length === 1 ? " result" : " results") + " in " + label;
        var grid = document.createElement("div");
        grid.className = "grid-products grid-products--3 mt-4";
        all.forEach(function (c) { grid.appendChild(c); });
        wrap.appendChild(grid);
        initReveal(grid);
        initWishlist();
        if (colourNames) applyColourVariants(grid, colourNames);
        return true;
      }).catch(function () { return false; });
    }
    if (rawTerm && !params.get("page")) {
      var fixedTerm = correctSearchQuery(rawTerm);
      var colourHit = colourMatch(fixedTerm);
      if (colourHit) {
        showListing([{ url: "/collections/all?filter.p.tag=" + encodeURIComponent(colourHit.tag), label: colourHit.name }], [colourHit.name])
          .then(function (ok) { if (ok) reveal(); else exactRedirect(); });
      } else if (aliasMatch(rawTerm) || aliasMatch(fixedTerm)) {
        var al = aliasMatch(rawTerm) || aliasMatch(fixedTerm);
        loadHay().then(function (hay) {
          var src = al.c.map(function (h) { return { url: h === SP_ALL ? "/collections/all" : "/collections/" + h, label: h }; });
          al.w.forEach(function (w) { src.push({ url: "/search?q=" + encodeURIComponent(w) + "&type=product&options%5Bprefix%5D=last", label: w, words: [w], hay: hay }); });
          src.label = al.l;
          return showListing(src);
        }).then(function (ok) { if (ok) reveal(); else exactRedirect(); });
      } else {
        collectionMatches(fixedTerm).then(function (cols) {
          if (!cols.length) { exactRedirect(); return; }
          showListing(cols.map(function (c) { return { url: "/collections/" + c.handle, label: c.title }; }))
            .then(function (ok) { if (ok) reveal(); else exactRedirect(); });
        });
      }
    } else { reveal(); }
    var cards = $$(".grid-products [data-product-card]");
    if (cards.length > 1 && params.get("q")) {
      var qWords = params.get("q").replace(/"/g, "").trim().toLowerCase().split(/\s+/).filter(Boolean);
      loadHay().then(function (hay) {
        if (showingCollection) return;
        function handleOf(c) { return (c.getAttribute("href") || "").split("?")[0].split("/").pop(); }
        var matching = cards.filter(function (c) {
          var h = hay[handleOf(c)] || ((($(".card__title", c) || {}).textContent) || "").toLowerCase();
          return qWords.every(function (w) { return h.indexOf(w) !== -1; });
        });
        if (matching.length && matching.length < cards.length) {
          cards.forEach(function (c) { if (matching.indexOf(c) === -1) c.remove(); });
          var cnt = $(".shop__count");
          if (cnt) cnt.textContent = matching.length + (matching.length === 1 ? " result" : " results");
        }
      });
    }
    var empty = $("[data-search-empty]");
    if (!empty) return;
    var q = empty.getAttribute("data-search-empty");
    if (!q) return;
    var corrected = correctSearchQuery(q);
    if (corrected.toLowerCase() === q.toLowerCase()) return;
    var base = (window.SilverPlay && window.SilverPlay.routes && window.SilverPlay.routes.search) || "/search";
    var a = document.createElement("a");
    a.className = "link-cta";
    a.href = base + "?q=" + encodeURIComponent(corrected);
    a.textContent = "Did you mean “" + corrected + "”?";
    var p = document.createElement("p");
    p.className = "mt-4";
    p.appendChild(a);
    empty.appendChild(p);
  }

  /* Contact page: the long admin text (h3 headings + paragraphs) becomes a
     grid of small cards under the form instead of one long column. */
  function initContactMore() {
    var box = $("[data-contact-more]");
    if (!box || !$("h3", box)) return;
    var intro = document.createElement("div"); intro.className = "contact__intro";
    var grid = document.createElement("div"); grid.className = "contact__cards";
    var card = null;
    Array.prototype.slice.call(box.childNodes).forEach(function (n) {
      if (n.nodeType !== 1) return;
      var tag = n.tagName.toLowerCase();
      if (tag === "h2") return;
      if (tag === "h3") {
        card = document.createElement("div"); card.className = "contact__card";
        card.appendChild(n); grid.appendChild(card);
      } else if (card) {
        card.appendChild(n);
      } else {
        intro.appendChild(n);
      }
    });
    box.textContent = "";
    if (intro.children.length) box.appendChild(intro);
    box.appendChild(grid);
    box.classList.add("contact__more--ready");
  }

  /* A page whose admin text uses h2 / h3 / h4 (title, group, question) --
     the FAQs page -- is turned into centred, collapsible question cards. */
  function initRteFaq() {
    var rte = $(".article__body .rte");
    if (!rte || !$("h4", rte) || !$("h3", rte)) return;
    var wrap = document.createElement("div");
    wrap.className = "faqx";
    var group = null, item = null, list = null;
    Array.prototype.slice.call(rte.childNodes).forEach(function (n) {
      if (n.nodeType !== 1) return;
      var tag = n.tagName.toLowerCase();
      if (tag === "h2") {
        /* the page banner already carries the page title -- drop this repeat */
        group = null; item = null; list = null;
      } else if (tag === "h3") {
        group = document.createElement("section"); group.className = "faqx__group";
        n.className = "faqx__group-title"; group.appendChild(n); wrap.appendChild(group); item = null;
        list = document.createElement("div"); list.className = "faqx__list"; group.appendChild(list);
      } else if (tag === "h4") {
        item = document.createElement("details"); item.className = "faqx__item";
        var sum = document.createElement("summary"); sum.textContent = n.textContent;
        var body = document.createElement("div"); body.className = "faqx__body";
        item.appendChild(sum); item.appendChild(body);
        if (!list) { list = document.createElement("div"); list.className = "faqx__list"; wrap.appendChild(list); }
        list.appendChild(item);
      } else if (item) {
        item.lastChild.appendChild(n);
      } else {
        (group || wrap).appendChild(n);
      }
    });
    rte.textContent = "";
    /* Group titles double as filter chips (same look as the blog page);
       "All" shows every group, a chip shows only its own questions. */
    var groups = $$(".faqx__group", wrap);
    if (groups.length > 1) {
      var chips = document.createElement("div");
      chips.className = "chips faqx__chips";
      var all = [];
      function setChip(active) {
        all.forEach(function (c) { c.el.setAttribute("aria-current", String(c.el === active.el)); });
        wrap.classList.toggle("faqx--single", active.group !== null);
        groups.forEach(function (g) { g.hidden = active.group !== null && g !== active.group; });
      }
      var allChip = { el: document.createElement("button"), group: null };
      allChip.el.type = "button"; allChip.el.className = "chip"; allChip.el.textContent = "All";
      all.push(allChip);
      groups.forEach(function (g) {
        var b = document.createElement("button");
        b.type = "button"; b.className = "chip"; b.textContent = $(".faqx__group-title", g).textContent;
        all.push({ el: b, group: g });
      });
      all.forEach(function (c) { c.el.addEventListener("click", function () { setChip(c); }); chips.appendChild(c.el); });
      setChip(allChip);
      rte.appendChild(chips);
    }
    rte.appendChild(wrap);
    rte.classList.add("rte--faq");
    var body = rte.closest(".article__body");
    if (body) body.classList.add("article__body--wide");
    var hero = $("[data-page-hero]");
    if (hero) hero.classList.add("pagehero--center");
    $$(".faqx__item", rte).forEach(function (d) {
      d.addEventListener("toggle", function () {
        if (!d.open) return;
        $$(".faqx__item[open]", rte).forEach(function (o) { if (o !== d) o.removeAttribute("open"); });
      });
    });
  }

  /* ------------------------------------------------------------- boot */
  function boot() {
    /* each init is isolated: one failing must not stop the rest (cart buttons, search ...) */
    var steps = [initRteFaq, initContactMore, initSplit, initReveal, initHeader, initHeaderHeight, initOfferReelHeight, initCineheroSlider, initMarquee, initTabs, initCineHero, initDeck, initFlip, initArc, initDisclosures, initDragRails, initKavach, initElegance, initStones, initGallery, initModals, initQuickView, initWishlist, initWishlistPage, initRecentlyViewed, initCart, initSearchDrawer, initSearchResultsPage, initColourVariantCards, initVariants, initSort, initFilters, initNotes, initReels, initPin, initGift, initCoinOffer];
    steps.forEach(function (fn) {
      try { fn(); } catch (err) { if (window.console) console.error("[theme] " + (fn.name || "init") + " failed:", err); }
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();

  // Theme editor support
  document.addEventListener("shopify:section:load", function (e) {
    initSplit(e.target); initReveal(e.target); boot();
  });
})();
